/**
 * AI Recruit - Job Submissions Tracking Component
 * Displays per-job submission metrics for assigned jobs
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Briefcase } from 'lucide-react'

export interface JobSubmissionData {
  job_uuid: string
  job_id?: string
  job_title: string
  client_name: string
  submitted_count: number
  pending_count: number
}

interface JobSubmissionsTrackingProps {
  jobSubmissions: JobSubmissionData[]
}

const JobSubmissionsTracking: React.FC<JobSubmissionsTrackingProps> = ({
  jobSubmissions
}) => {
  const navigate = useNavigate()

  const handleJobClick = (jobUuid: string) => {
    navigate(`/jobs/${jobUuid}?tab=matching`)
  }

  const isEmpty = !jobSubmissions || jobSubmissions.length === 0

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium text-gray-900">Job Submissions Tracking</h3>
      </div>
      {isEmpty ? (
        <div className="card-body">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Briefcase className="w-12 h-12 text-gray-400 mb-3" aria-hidden="true" />
            <p className="text-gray-600 text-sm">You don't have any job assignments yet</p>
          </div>
        </div>
      ) : (
        <div className="card-body p-0">
          <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Job ID
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Job Title
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Client
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobSubmissions.map((submission) => (
                  <tr 
                    key={submission.job_uuid} 
                    className={`hover:bg-gray-50 ${submission.submitted_count === 0 ? 'bg-red-50' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleJobClick(submission.job_uuid)}
                        className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline cursor-pointer"
                      >
                        {submission.job_id || submission.job_uuid.slice(0, 8)}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {submission.job_title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {submission.client_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {submission.submitted_count}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default JobSubmissionsTracking
